import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { timeEntryKeys } from "@/lib/query";

export interface TimeEntry {
  id: string;
  userId: string;
  entryDate: string;
  type: string;
  woNumber: string | null;
  category: string | null;
  subtask: string | null;
  overheadCode: string | null;
  overheadSub: string | null;
  overheadCategoryId: string | null;
  overheadSubcategoryId: string | null;
  hours: number;
  notes: string | null;
  status: string;
  flagManager: string | null;
  flagNote: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HistorySummary {
  totalEntries: number;
  totalHours: number;
  billableHours: number;
  overheadHours: number;
}

export interface WeeklySummary {
  total: number;
  billable: number;
  overhead: number;
  pct: number | null;
  flaggedCount: number;
  entries: TimeEntry[];
}

interface TimeEntriesListResponse {
  data: TimeEntry[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface CreateTimeEntryInput {
  entryDate: string;
  type: string;
  woNumber?: string | null;
  category?: string | null;
  subtask?: string | null;
  overheadCode?: string | null;
  overheadSub?: string | null;
  hours: number;
  notes?: string | null;
}

export interface UpdateTimeEntryInput {
  entryDate?: string;
  type?: string;
  woNumber?: string | null;
  category?: string | null;
  subtask?: string | null;
  overheadCode?: string | null;
  overheadSub?: string | null;
  hours?: number;
  notes?: string | null;
  status?: string;
}

export interface ListParams {
  week?: string;
  status?: string;
  type?: string;
  dateFrom?: string;
  dateTo?: string;
  woNumber?: string;
  categoryId?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}

function buildSearch(params: Record<string, unknown>): URLSearchParams {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    search.set(k, String(v));
  }
  return search;
}

export function useTimeEntries(params: ListParams = {}) {
  const search = buildSearch(params as Record<string, unknown>);

  return useQuery({
    queryKey: timeEntryKeys.list(params as Record<string, unknown>),
    queryFn: async (): Promise<TimeEntriesListResponse> => {
      const res = await fetch(`/api/time-entries?${search.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch time entries");
      return res.json() as Promise<TimeEntriesListResponse>;
    },
  });
}

export function useHistorySummary(filters: Omit<ListParams, "limit" | "cursor">) {
  const search = buildSearch(filters as Record<string, unknown>);

  return useQuery({
    queryKey: timeEntryKeys.historySummary(filters as Record<string, unknown>),
    queryFn: async (): Promise<HistorySummary> => {
      const res = await fetch(`/api/time-entries/history-summary?${search.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch history summary");
      return res.json() as Promise<HistorySummary>;
    },
  });
}

export function useWeeklySummary(week: string) {
  return useQuery({
    queryKey: timeEntryKeys.weeklySummary(week),
    queryFn: async (): Promise<WeeklySummary> => {
      const res = await fetch(`/api/time-entries/weekly-summary?week=${week}`);
      if (!res.ok) throw new Error("Failed to fetch weekly summary");
      return res.json() as Promise<WeeklySummary>;
    },
    enabled: !!week,
  });
}

export function useCreateTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTimeEntryInput) => {
      const res = await fetch("/api/time-entries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to create time entry");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      toast.success("Time entry saved");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useUpdateTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTimeEntryInput }) => {
      const res = await fetch(`/api/time-entries/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to update time entry");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      toast.success("Time entry updated");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/time-entries/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to delete time entry");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      toast.success("Time entry deleted");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useSubmitWeek() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (week: string) => {
      const res = await fetch("/api/time-entries/submit-week", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ week }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to submit week");
      }
      return res.json() as Promise<{ count: number }>;
    },
    onSuccess: (data: { count: number }) => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
      toast.success(`${data.count} ${data.count === 1 ? "entry" : "entries"} submitted`);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
